import React from 'react';
import styles from './page.module.css';

/**
 * Documentation: document ingestion — uniform collapsible sections only (grouped by h2).
 */
export function IngestionDocumentationSection() {
  return (
    <section className={styles.section} aria-labelledby="ingestion-title">
      <h1 id="ingestion-title" className={styles.title}>
        Document ingestion
      </h1>
      <p className={styles.subtitle}>
        How documents become searchable knowledge—open a section below to read more.
      </p>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>Overview</h2>

        <details className={styles.details}>
          <summary>In plain terms</summary>
          <p className={styles.detailsText}>
            We read your document (including scanned PDFs), break it into meaningful pieces, enrich each piece
            so search and AI understand it better, turn those pieces into embeddings (numerical fingerprints
            for similarity), and optionally save everything to your project or knowledge base so assistants
            and humans can find the right passage later.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Why this matters for structured documentation teams</summary>
          <p className={styles.detailsText}>
            <strong>Traceability.</strong> Content is tied to a document and section path, not only a blob of
            text, so answers can be checked against the source. This is useful in any regulated or quality-driven
            context (GxP, ISO 9001, automotive ASPICE, ITIL, internal QMS, etc.) without being tied to one.
          </p>
          <p className={styles.detailsText}>
            <strong>Precision.</strong> Chunks follow structure (headings, tables, lists) where possible, so
            retrieval matches how real specification and procedure documents are written.
          </p>
          <p className={styles.detailsText}>
            <strong>Less noise.</strong> Deduplication and importance hints reduce redundant or low-value
            matches before they reach the assistant.
          </p>
        </details>

        <details className={styles.details}>
          <summary>What gets stored (conceptually)</summary>
          <p className={styles.detailsText}>
            When persistence is enabled, each saved row typically includes the text snippet, the main embedding
            vector, indexing fields (document id, order), and additional fields such as chunk type, section
            path, retrieval hint, entity references, importance score, and hashes for deduplication.
          </p>
        </details>
      </div>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>How it works</h2>

        <details className={styles.details}>
          <summary>1. Bring text in</summary>
          <p className={styles.detailsText}>
            You provide either raw text or a file link. PDFs and page images are turned into text using Google
            Cloud Vision (OCR), using the same path as elsewhere for knowledge uploads.
          </p>
        </details>

        <details className={styles.details}>
          <summary>2. Clean up for structure</summary>
          <p className={styles.detailsText}>
            Very long lines from OCR are split so headings and sections can be detected reliably on large
            documents.
          </p>
        </details>

        <details className={styles.details}>
          <summary>3. Split intelligently</summary>
          <p className={styles.detailsText}>
            First by document structure (headings, numbered sections, tables, bullet groups). Large sections are
            split further with a small, fast model so each piece stays semantically coherent. Optional smaller
            &quot;micro&quot; pieces can improve pinpoint search.
          </p>
        </details>

        <details className={styles.details}>
          <summary>4. Enrich each piece</summary>
          <p className={styles.detailsText}>
            A lightweight model adds a short &quot;what questions does this answer?&quot; line, importance for
            compliance and risk, and detects references like URS-001 or RISK-003 when they appear in the text.
          </p>
        </details>

        <details className={styles.details}>
          <summary>5. Remove duplicates</summary>
          <p className={styles.detailsText}>
            Near-duplicate chunks are dropped so storage and search stay fast and less repetitive.
          </p>
        </details>

        <details className={styles.details}>
          <summary>6. Build embeddings</summary>
          <p className={styles.detailsText}>
            Each chunk is embedded with context (document type, section, entities) so similarity search matches
            intent, not only raw wording. A second embedding can be stored for the enrichment line when that
            option is enabled.
          </p>
        </details>

        <details className={styles.details}>
          <summary>7. Save (optional)</summary>
          <p className={styles.detailsText}>
            If you turn on persistence, vectors and metadata are written to Firestore in the same collections
            the rest of the app uses for knowledge and projects, so existing features keep working.
          </p>
        </details>
      </div>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>For engineers</h2>

        <details className={styles.details}>
          <summary>Scope and stack</summary>
          <p className={styles.detailsText}>
            Cloud Function <code className={styles.inlineCode}>ingestDocument</code> in{' '}
            <code className={styles.inlineCode}>functions/src/ingestDocument.ts</code> orchestrates{' '}
            <code className={styles.inlineCode}>functions/src/ingestion/</code>. Chat-style steps use Phi;
            embeddings use TEI (OpenAI-compatible). Defaults match a typical docker-compose layout (Phi on
            port 8002, TEI embeddings on 8080) unless overridden by environment variables.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Entry point and HTTP contract</summary>
          <p className={styles.detailsText}>
            <strong>Export.</strong> <code className={styles.inlineCode}>ingestDocument</code>. Region
            europe-west3, timeout 540s, memory 4GiB.
          </p>
          <p className={styles.detailsText}>
            <strong>Body.</strong> JSON <code className={styles.inlineCode}>POST</code>. Send either{' '}
            <code className={styles.inlineCode}>text</code> or <code className={styles.inlineCode}>fileUrl</code>.
            With <code className={styles.inlineCode}>fileUrl</code>, <code className={styles.inlineCode}>userId</code> is
            required (Storage and OCR path conventions).
          </p>
          <p className={styles.detailsText}>
            <strong>meta.</strong> Optional <code className={styles.inlineCode}>documentId</code>,{' '}
            <code className={styles.inlineCode}>projectId</code>, <code className={styles.inlineCode}>knowledgeId</code>,{' '}
            <code className={styles.inlineCode}>companyId</code>, <code className={styles.inlineCode}>docType</code>,{' '}
            <code className={styles.inlineCode}>docTitle</code>, <code className={styles.inlineCode}>language</code>,{' '}
            <code className={styles.inlineCode}>filename</code>, <code className={styles.inlineCode}>sourceFileUrl</code>.
            Do not set both <code className={styles.inlineCode}>projectId</code> and{' '}
            <code className={styles.inlineCode}>knowledgeId</code>.
          </p>
          <p className={styles.detailsText}>
            <strong>options.</strong> Partial pipeline options (see &quot;Pipeline options&quot; below).
          </p>
          <p className={styles.detailsText}>
            <strong>persist.</strong>{' '}
            <code className={styles.inlineCode}>&#123; enabled, replaceExistingChunks?, omitEmbeddingsInResponse? &#125;</code>.
            When <code className={styles.inlineCode}>enabled</code> is true, <code className={styles.inlineCode}>userId</code> is
            required.
          </p>
          <p className={styles.detailsText}>
            <strong>Database.</strong> Firestore{' '}
            <code className={styles.inlineCode}>thescon-production-db</code> for writes, consistent with other
            production writers.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Pipeline stages (code modules)</summary>
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Module / notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Extract text</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/extract.ts</code> — Vision async PDF batch or
                    page JPG OCR; shared with legacy knowledge injection.
                  </td>
                </tr>
                <tr>
                  <td>Normalize OCR</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/normalize.ts</code> — break overlong lines (
                    <code className={styles.inlineCode}>ocrLongLineBreakEvery</code>).
                  </td>
                </tr>
                <tr>
                  <td>Structural split</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/structure.ts</code> — headings, numbering,
                    tables, lists.
                  </td>
                </tr>
                <tr>
                  <td>Semantic split</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/semantic.ts</code> — Phi JSON split + fallback
                    when over token threshold.
                  </td>
                </tr>
                <tr>
                  <td>Micro segments</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/micro.ts</code> — optional smaller spans.
                  </td>
                </tr>
                <tr>
                  <td>Enrich</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/enrich.ts</code> — batched Phi calls (
                    <code className={styles.inlineCode}>enrichBatchSize</code>); hints, importance, entity refs.
                  </td>
                </tr>
                <tr>
                  <td>Dedup</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/dedup.ts</code> — hash + Jaccard-style overlap.
                  </td>
                </tr>
                <tr>
                  <td>Embed</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/embedding.ts</code> — TEI batching;
                    context-enriched embedding strings.
                  </td>
                </tr>
                <tr>
                  <td>Orchestration</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/pipeline.ts</code> —{' '}
                    <code className={styles.inlineCode}>runIngestPipeline</code>.
                  </td>
                </tr>
                <tr>
                  <td>Persist</td>
                  <td>
                    <code className={styles.inlineCode}>ingestion/firestorePersist.ts</code> — batched writes;
                    optional delete by <code className={styles.inlineCode}>documentId</code>; knowledge merge
                    summary.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>

        <details className={styles.details}>
          <summary>Environment variables (ingestion stack)</summary>
          <p className={styles.detailsText}>
            Override when the reverse proxy or host changes (for example after moving to a new server).
            Defaults align with local docker-compose ports: Phi chat <code className={styles.inlineCode}>:8002</code>,
            TEI embeddings <code className={styles.inlineCode}>:8080</code>.
          </p>
          <p className={styles.detailsText}>
            <code className={styles.inlineCode}>INGEST_PHI_CHAT_URL</code>,{' '}
            <code className={styles.inlineCode}>INGEST_PHI_MODEL</code>,{' '}
            <code className={styles.inlineCode}>INGEST_EMBED_URL</code>,{' '}
            <code className={styles.inlineCode}>INGEST_EMBED_MODEL</code>,{' '}
            <code className={styles.inlineCode}>INGEST_STACK_AUTH</code>.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Firestore paths (when persist is enabled)</summary>
          <p className={styles.detailsText}>
            <strong>Knowledge base.</strong>{' '}
            <code className={styles.inlineCode}>knowledge/&#123;knowledgeId&#125;/documentKnowledge</code>
          </p>
          <p className={styles.detailsText}>
            <strong>Project.</strong>{' '}
            <code className={styles.inlineCode}>projects/&#123;projectId&#125;/documentChunks</code>
          </p>
          <p className={styles.detailsText}>
            <strong>Legacy top-level.</strong>{' '}
            <code className={styles.inlineCode}>documentChunks</code> when neither project nor knowledge id is
            set.
          </p>
          <p className={styles.detailsText}>
            <strong>Knowledge document metadata.</strong>{' '}
            <code className={styles.inlineCode}>knowledge/&#123;knowledgeId&#125;/documents/&#123;documentId&#125;</code> — merge
            can include an <code className={styles.inlineCode}>ingestV2</code> summary when applicable.
          </p>
          <p className={styles.detailsText}>
            Chunk documents include legacy fields plus v2 fields such as{' '}
            <code className={styles.inlineCode}>chunkType</code>, <code className={styles.inlineCode}>sectionPath</code>,{' '}
            <code className={styles.inlineCode}>retrievalHint</code>,{' '}
            <code className={styles.inlineCode}>ingestPipelineVersion</code> (for example{' '}
            <code className={styles.inlineCode}>ingestion-1</code> on chunk rows).
          </p>
        </details>

        <details className={styles.details}>
          <summary>Pipeline options (defaults and guardrails)</summary>
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>enableStructuralLayer</code>,{' '}
                    <code className={styles.inlineCode}>enableSemanticLayer</code>,{' '}
                    <code className={styles.inlineCode}>enableMicroLayer</code>
                  </td>
                  <td>Toggle layers.</td>
                </tr>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>enableEnrichment</code>,{' '}
                    <code className={styles.inlineCode}>embedRetrievalHints</code>
                  </td>
                  <td>Phi enrichment; optional second TEI pass for hint text.</td>
                </tr>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>semanticMaxTokens</code>,{' '}
                    <code className={styles.inlineCode}>microMaxTokens</code>,{' '}
                    <code className={styles.inlineCode}>semanticSplitThresholdTokens</code>
                  </td>
                  <td>Sizing and when to run semantic split.</td>
                </tr>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>maxInputChars</code>, <code className={styles.inlineCode}>maxChunks</code>
                  </td>
                  <td>Fail-fast limits for very large inputs.</td>
                </tr>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>enrichBatchSize</code>
                  </td>
                  <td>Chunks per Phi enrichment request (small batches for 8k context models).</td>
                </tr>
                <tr>
                  <td>
                    <code className={styles.inlineCode}>ocrLongLineBreakEvery</code>
                  </td>
                  <td>Insert line breaks for OCR mega-lines.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>

        <details className={styles.details}>
          <summary>Pipeline flow (one line)</summary>
          <pre className={styles.codeBlock} tabIndex={0}>
            {`fileUrl|text → extract (Vision?) → normalize → structural → semantic? → micro?
→ enrich (Phi, batched) → dedup → embed (TEI, batched) → [persist Firestore]`}
          </pre>
        </details>

        <details className={styles.details}>
          <summary>Operational notes</summary>
          <p className={styles.detailsText}>
            <strong>Reranking.</strong> The TEI reranker in the stack is used at query time, not inside this
            ingestion function.
          </p>
          <p className={styles.detailsText}>
            <strong>Response size.</strong> Use <code className={styles.inlineCode}>omitEmbeddingsInResponse</code> when
            vectors are already in Firestore to avoid huge JSON payloads.
          </p>
          <p className={styles.detailsText}>
            <strong>Very large PDFs.</strong> A single Cloud Function run is bounded by timeout and memory;
            extreme documents may need a future job queue or sharding.
          </p>
          <p className={styles.detailsText}>
            <strong>Logging.</strong> The pipeline logs{' '}
            <code className={styles.inlineCode}>[ingestion] pipeline counts</code> after deduplication for Cloud
            Logging.
          </p>
        </details>
      </div>
    </section>
  );
}
